import React, { useEffect, useState, useRef } from 'react'
import { Row, Col, Card, Button, Typography, Space, Statistic, Carousel, Badge } from 'antd'
import { 
  VideoCameraOutlined, 
  PictureOutlined, 
  RocketOutlined, 
  StarOutlined,
  ThunderboltOutlined,
  CrownOutlined,
  PlayCircleOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  FireOutlined,
  TrophyOutlined,
  SafetyOutlined,
  EditOutlined,
  BulbOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { apiClient } from '@/services/api'
import { on as onEvent } from '@/services/events'
import { useSiteConfigStore } from '@/store/siteConfigStore'
import { GlobalStyles, COMMON_STYLES } from '@/styles/globalStyles'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import SectionSkeleton from '@/components/Common/SectionSkeleton'
import LazyImage from '@/components/Common/LazyImage'
import '@/styles/global.css'

const { Title, Paragraph, Text } = Typography

const heroVideos = [
  '/assets/hero/background-1.mp4',
  '/assets/hero/background-2.mp4',
  '/assets/hero/background-3.mp4',
  '/assets/hero/background-4.mp4'
]

const HomePage: React.FC = () => {
  const navigate = useNavigate()
  const { config: siteConfig, load: loadSiteConfig } = useSiteConfigStore()
  
  // 页面初始加载状态 - 控制占位图和渐进显示
  const [initialLoad, setInitialLoad] = useState(true)
  const [visibleSections, setVisibleSections] = useState({
    hero: false,
    aiModels: false,
    coreFeatures: false,
    videoDemo: false,
    imageDemo: false,
    promptPolish: false,
    advantages: false,
    userReviews: false,
    stats: false,
    cta: false
  })
  
  // 使用 ref 来检测各个 section 是否进入视口
  const aiModelsRef = useRef<HTMLDivElement>(null)
  const coreFeaturesRef = useRef<HTMLDivElement>(null)
  const videoDemoRef = useRef<HTMLDivElement>(null)
  const imageDemoRef = useRef<HTMLDivElement>(null)
  const promptPolishRef = useRef<HTMLDivElement>(null)
  const advantagesRef = useRef<HTMLDivElement>(null)
  const userReviewsRef = useRef<HTMLDivElement>(null)
  const statsRef = useRef<HTMLDivElement>(null)
  const ctaRef = useRef<HTMLDivElement>(null)

  // 使用 Intersection Observer 检测各个 section 是否进入视口
  const aiModelsVisible = useIntersectionObserver(aiModelsRef, { threshold: 0.1, rootMargin: '100px' })
  const coreFeaturesVisible = useIntersectionObserver(coreFeaturesRef, { threshold: 0.1, rootMargin: '100px' })
  const videoDemoVisible = useIntersectionObserver(videoDemoRef, { threshold: 0.1, rootMargin: '100px' })
  const imageDemoVisible = useIntersectionObserver(imageDemoRef, { threshold: 0.1, rootMargin: '100px' })
  const promptPolishVisible = useIntersectionObserver(promptPolishRef, { threshold: 0.1, rootMargin: '100px' })
  const advantagesVisible = useIntersectionObserver(advantagesRef, { threshold: 0.1, rootMargin: '100px' })
  const userReviewsVisible = useIntersectionObserver(userReviewsRef, { threshold: 0.1, rootMargin: '100px' })
  const statsVisible = useIntersectionObserver(statsRef, { threshold: 0.1, rootMargin: '100px' })
  const ctaVisible = useIntersectionObserver(ctaRef, { threshold: 0.1, rootMargin: '100px' })
  
  // 初始加载：显示占位图，然后从上到下依次显示各个section
  useEffect(() => {
    if (!initialLoad) return
    
    // 立即显示Hero区域
    setVisibleSections(prev => ({ ...prev, hero: true }))
    
    // 依次显示各个section，每个间隔200ms
    const timers = [
      setTimeout(() => setVisibleSections(prev => ({ ...prev, aiModels: true })), 300),
      setTimeout(() => setVisibleSections(prev => ({ ...prev, coreFeatures: true })), 500),
      setTimeout(() => setVisibleSections(prev => ({ ...prev, videoDemo: true })), 700),
      setTimeout(() => setVisibleSections(prev => ({ ...prev, imageDemo: true })), 900),
      setTimeout(() => setVisibleSections(prev => ({ ...prev, promptPolish: true })), 1100),
      setTimeout(() => setVisibleSections(prev => ({ ...prev, advantages: true })), 1300),
      setTimeout(() => setVisibleSections(prev => ({ ...prev, userReviews: true })), 1500),
      setTimeout(() => setVisibleSections(prev => ({ ...prev, stats: true })), 1700),
      setTimeout(() => {
        setVisibleSections(prev => ({ ...prev, cta: true }))
        setInitialLoad(false) // 所有section都显示后，关闭初始加载状态
      }, 1900)
    ]
    
    return () => {
      timers.forEach(timer => clearTimeout(timer))
    }
  }, [initialLoad])
  
  // 平台统计数据
  const [platformStats, setPlatformStats] = useState({
    totalUsers: 0,
    totalGenerations: 0,
    satisfactionRate: 98,
    avgProcessingTime: 25
  })
  
  // 加载站点配置（延迟加载，不影响Banner展示）
  useEffect(() => {
    // 延迟加载配置，让Banner先显示
    const timer = setTimeout(() => {
    loadSiteConfig()
    }, 100)
    
    // 监听配置更新事件，自动重新加载
    const unsubscribe = onEvent('config_updated', (data: any) => {
      if (data.type === 'site_config' || data.type === 'system_config') {
        console.log('🔄 [首页] 检测到配置更新，重新加载...')
        loadSiteConfig()
      }
    })
    
    return () => {
      clearTimeout(timer)
      // 清理事件监听（如果events.ts支持取消订阅）
    }
  }, [loadSiteConfig])

  // 获取平台统计数据（延迟加载）
  useEffect(() => {
    // 延迟获取统计数据，先让页面显示
    const timer = setTimeout(() => {
    const fetchStats = async () => {
      try {
        const res = await apiClient.get('/public/stats')
        if (res.success && res.data) {
          setPlatformStats({
            totalUsers: res.data.totalUsers || 0,
            totalGenerations: res.data.totalGenerations || 0,
            satisfactionRate: res.data.satisfactionRate || 98,
            avgProcessingTime: res.data.avgProcessingTime || 25
          })
        }
      } catch (e) {
        // 使用默认值
        console.log('获取统计数据失败，使用默认值')
      }
    }
    fetchStats()

    // 监听SSE事件，动态刷新首页统计
    const refresh = () => fetchStats()
    onEvent('user_updated', refresh)
    onEvent('video_updated', refresh)
    onEvent('image_updated', refresh)
    onEvent('payment_updated', refresh)
    }, 300) // 延迟300ms

    return () => clearTimeout(timer)
  }, [])
  
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [heroVideoReady, setHeroVideoReady] = useState(false)
  const [videoLoading, setVideoLoading] = useState(true)
  const [videoError, setVideoError] = useState(false)
  const [videoLoadedCount, setVideoLoadedCount] = useState(0)
  
  // 移除旧的逐步加载逻辑，改用 Intersection Observer

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY })
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  const features = [
    {
      icon: <VideoCameraOutlined style={{ fontSize: 'var(--font-h1-mobile)' }} className="text-blue-500" />,
      title: 'AI视频生成',
      description: '使用先进的Sora2技术，从文字描述生成高质量视频内容',
      badge: '热门',
      color: 'blue',
      action: () => navigate('/video')
    },
    {
      icon: <PictureOutlined style={{ fontSize: 'var(--font-h2-mobile)' }} className="text-purple-500" />,
      title: 'AI图像生成',
      description: '基于最新AI模型，快速生成各种风格的图像作品',
      badge: '推荐',
      color: 'purple',
      action: () => navigate('/image')
    },
    {
      icon: <EditOutlined style={{ fontSize: 'var(--font-h1-mobile)' }} className="text-cyan-500" />,
      title: '提示词润色补全',
      description: 'AI智能优化提示词，让您的创作描述更加专业和详细',
      badge: 'AI',
      color: 'cyan',
      action: () => navigate('/video')
    }
  ]

  // AI模型展示数据
  const aiModels = [
    {
      name: '索拉2',
      nameEn: 'Sora2',
      icon: '☁️',
      description: '旗舰多模态视频生成模型,可实现电影般的真实感、无缝的声音以及对角色、对话和动作的完全控制。',
      bgColor: '#1e3a8a', // 深蓝色（保留，用于特殊模型卡片）
      bgStyle: {
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
        position: 'relative' as const,
        overflow: 'hidden' as const
      },
      textColor: 'var(--color-bg-white)',
      iconBg: 'rgba(255, 255, 255, 0.1)'
    },
    {
      name: '纳米香蕉',
      nameEn: 'Nano Banana',
      icon: '🍌',
      description: '先进的图像生成和编辑模型,可实现精确、高分辨率的视觉控制和逼真的结果。',
      bgColor: '#fef3c7', // 浅黄色
      bgStyle: {
        background: '#fef3c7'
      },
      textColor: '#000000',
      iconBg: 'rgba(0, 0, 0, 0.05)'
    },
    {
      name: '维奥3.1',
      nameEn: 'VEO 3.1',
      logo: 'Google',
      description: '一种多模态视频模型,用于生成具有细节、沉浸式和逼真的视频旁白的同步运动和音频。',
      bgColor: 'gradient',
      bgStyle: {
        background: 'linear-gradient(135deg, #ec4899 0%, #f97316 50%, #a855f7 100%)'
      },
      textColor: '#ffffff',
      iconBg: 'rgba(255, 255, 255, 0.1)'
    },
    {
      name: 'Seedream 4.0',
      nameEn: 'Seedream 4.0',
      icon: '✨',
      description: '于生成和编辑的统一图像模型,具有详细、一致且视觉丰富的创意输出。',
      bgColor: 'gradient',
      bgStyle: {
        background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 25%, #3b82f6 50%, #10b981 75%, #f97316 100%)',
        position: 'relative' as const,
        overflow: 'hidden' as const
      },
      textColor: '#ffffff',
      iconBg: 'rgba(255, 255, 255, 0.15)'
    }
  ]

  // 统计信息基础值（用于显示）
  // 用户数量：1.68万 = 16800，显示值 = 16800 + 真实值
  // 生成作品：5.48万 = 548000（548487 - 487），显示值 = 548000 + 真实值
  const BASE_USERS = 16800; // 1.68万的基础值
  const BASE_GENERATIONS = 548000; // 5.48万的基础值（548487 - 487 = 548000）
  
  // 计算显示值：基础值 + 真实值
  const displayUsers = BASE_USERS + platformStats.totalUsers;
  const displayGenerations = BASE_GENERATIONS + platformStats.totalGenerations;

  const stats = [
    { 
      title: '用户数量', 
      value: displayUsers >= 10000 ? displayUsers.toLocaleString() : displayUsers.toString(), 
      suffix: '人' 
    },
    { 
      title: '生成作品', 
      value: displayGenerations >= 100000 ? displayGenerations.toLocaleString() : displayGenerations.toLocaleString(), 
      suffix: '个' 
    },
    { 
      title: '满意度', 
      value: platformStats.satisfactionRate.toString(), 
      suffix: '%' 
    },
    { 
      title: '处理速度', 
      value: `< ${platformStats.avgProcessingTime}`, 
      suffix: '秒' 
    }
  ]

  const testimonials = [
    {
      name: '张设计师',
      role: 'UI/UX设计师',
      content: 'Sora2 AI让我的创意工作变得更加高效，从想法到成品只需要几分钟。',
      avatar: '👨‍💻'
    },
    {
      name: '李创作者',
      role: '内容创作者',
      content: '视频生成功能太棒了！生成的视频质量超出我的预期，完全满足我的需求。',
      avatar: '👩‍🎨'
    },
    {
      name: '王老师',
      role: '教育工作者',
      content: '图像生成功能帮助我快速制作教学素材，大大提升了教学效率。',
      avatar: '👨‍🏫'
    }
  ]

  return (
    <div className="min-h-screen w-full" style={{ background: 'transparent' }}>
      {/* 动态背景装饰 - 仅在英雄区域外显示 */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0" style={{ zIndex: -1 }}>
        <motion.div
          className="absolute w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
          animate={{
            x: mousePosition.x * 0.02,
            y: mousePosition.y * 0.02,
          }}
          style={{ top: '-10%', left: '-10%' }}
        />
        <motion.div
          className="absolute w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
          animate={{
            x: mousePosition.x * -0.02,
            y: mousePosition.y * -0.02,
          }}
          style={{ top: '50%', right: '-10%' }}
        />
        <motion.div
          className="absolute w-96 h-96 bg-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
          animate={{
            x: mousePosition.x * 0.015,
            y: mousePosition.y * 0.015,
          }}
          style={{ bottom: '-10%', left: '30%' }}
        />
      </div>

      <div className="relative z-10" style={{ background: 'transparent' }}>
        {/* 英雄区域 - 应用统一样式，带背景图片 */}
        <section 
          className="py-12 md:py-24 px-4 relative overflow-hidden banner-hero"
          style={{
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          {/* 渐变蓝色默认背景 - 始终显示，防止白色背景 */}
          <div 
            className="absolute inset-0 hero-gradient-fallback"
            style={{
              zIndex: 0,
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #6366f1 100%)',
              pointerEvents: 'none'
            }}
          />
          
          {/* 背景视频层 - 在渐变背景上层，z-index: 1 */}
          <div 
            className="absolute inset-0 overflow-hidden"
            style={{
              zIndex: 1,
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: heroVideoReady && !videoError ? 1 : 0,
              transition: 'opacity 0.8s ease-in-out',
              pointerEvents: 'none'
            }}
          >
            <Carousel 
              autoplay 
              autoplaySpeed={6000} 
              pauseOnHover={false} 
              pauseOnFocus={false} 
              dots={false} 
              effect="fade"
            >
              {heroVideos.map((src, index) => (
                <div key={src} className="w-full h-full" style={{ width: '100%', display: 'inline-block' }}>
                  <video
                    src={src}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover"
                    onLoadedData={(e) => {
                      const video = e.currentTarget
                      video.play().catch((error) => {
                        console.warn('视频自动播放失败:', error)
                      })
                      
                      // 延迟一点确保视频准备就绪
                      setTimeout(() => {
                        setHeroVideoReady(true)
                        setVideoLoading(false)
                      }, 300)
                    }}
                    onError={() => {
                      setVideoError(true)
                      setVideoLoading(false)
                      setHeroVideoReady(false)
                    }}
                    onCanPlay={() => {
                      if (!heroVideoReady) {
                        setVideoLoading(false)
                      }
                    }}
                  />
                </div>
              ))}
            </Carousel>
          </div>
          
          {/* 内容层 - 在视频上层，z-index: 10 */}
          <div 
            className="unified-container relative w-full"
            style={{
              zIndex: 10,
              position: 'relative'
            }}
          >
            <div className="text-center" style={{ marginBottom: 'var(--spacing-xl)' }}>
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                <motion.div 
                  className="inline-block" 
                  style={{
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    marginBottom: '24px',
                    background: 'rgba(255, 192, 203, 0.9)',
                    borderRadius: '8px',
                    display: 'inline-block'
                  }}
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Text className="text-xs md:text-sm font-medium" style={{ color: '#1f2937', fontWeight: 500 }}>
                    🚀 基于OpenAI最新技术AI驱动
                  </Text>
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ 
                  duration: 1, 
                  delay: 0.1,
                  ease: [0.25, 0.46, 0.45, 0.94],
                  type: "spring",
                  stiffness: 100
                }}
              >
                <Title 
                  level={1} 
                  className="unified-title-h1"
                  style={{
                    marginBottom: 'var(--spacing-lg)',
                    paddingLeft: 'var(--spacing-sm)',
                    paddingRight: 'var(--spacing-sm)',
                    lineHeight: 1.2, 
                    fontSize: 'clamp(2.5rem, 8vw, 5.5rem)',
                    textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                    color: '#ffffff',
                    fontWeight: 700
                  }}
                >
                  {siteConfig?.siteName || 'Sora2 AI Platform'}
                </Title>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.8, 
                  delay: 0.3,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
              >
                <Title 
                  level={2} 
                  className="text-responsive-base font-normal"
                  style={{
                    marginBottom: 'var(--spacing-lg)',
                    paddingLeft: 'var(--spacing-sm)',
                    paddingRight: 'var(--spacing-sm)',
                    lineHeight: 1.4,
                    color: 'var(--color-bg-white)',
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  {siteConfig?.siteDescription || '让创意触手可及，让想象成为现实'}
                </Title>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                <Paragraph 
                  className="text-responsive-base max-w-3xl mx-auto leading-relaxed"
                  style={{
                    marginBottom: 'var(--spacing-xl)',
                    paddingLeft: 'var(--spacing-lg)',
                    paddingRight: 'var(--spacing-lg)',
                    color: 'rgba(255, 255, 255, 0.95)',
                    textShadow: '0 1px 5px rgba(0, 0, 0, 0.2)'
                  }}
                >
                  用<strong style={{ color: 'var(--color-bg-white)', fontWeight: 600 }}>文字</strong>创造惊艳的<strong style={{ color: 'var(--color-bg-white)', fontWeight: 600 }}>视频</strong>和<strong style={{ color: 'var(--color-bg-white)', fontWeight: 600 }}>图像</strong>，
                  <span style={{ color: 'var(--color-bg-white)', fontWeight: 600 }}>让想象成为现实</span>
                </Paragraph>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                <Space size="middle" wrap className="flex justify-center" style={{ marginBottom: 'var(--spacing-xl)' }}>
                  <motion.div
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Button 
                      type="primary" 
                      size="large"
                      icon={<VideoCameraOutlined />}
                      onClick={() => navigate('/video')}
                      className="unified-button"
                      style={{
                        height: '48px',
                        paddingLeft: '32px',
                        paddingRight: '32px',
                        fontSize: '16px',
                        fontWeight: 500,
                        borderRadius: '8px',
                        backgroundColor: '#3b82f6',
                        borderColor: '#3b82f6',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
                      }}
                    >
                      生成视频
                    </Button>
                  </motion.div>
                  
                  <motion.div
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Button 
                      size="large"
                      icon={<PictureOutlined />}
                      onClick={() => navigate('/image')}
                      className="unified-button"
                      style={{
                        height: '48px',
                        paddingLeft: '32px',
                        paddingRight: '32px',
                        fontSize: '16px',
                        fontWeight: 500,
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        color: '#1f2937',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      生成图像
                    </Button>
                  </motion.div>
                </Space>

                <motion.div 
                  className="flex justify-center text-responsive-sm flex-wrap"
                  style={{ 
                    gap: 'var(--spacing-xl)',
                    color: 'rgba(255, 255, 255, 0.9)', 
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                >
                  <motion.span
                    whileHover={{ scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 400 }}
                    style={{ color: '#ffffff' }}
                  >
                    <span style={{ color: '#10b981', marginRight: '4px' }}>✓</span> 免费试用
                  </motion.span>
                  <motion.span
                    whileHover={{ scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 400 }}
                    style={{ color: '#ffffff' }}
                  >
                    <span style={{ color: '#10b981', marginRight: '4px' }}>✓</span> 无需信用卡
                  </motion.span>
                  <motion.span
                    whileHover={{ scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 400 }}
                    style={{ color: '#ffffff' }}
                  >
                    <span style={{ color: '#10b981', marginRight: '4px' }}>✓</span> 即刻开始
                  </motion.span>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* AI模型展示 - 使用渐进式加载 */}
        <section ref={aiModelsRef} className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-hidden"
          style={{
            paddingTop: 'var(--spacing-xxl)',
            paddingBottom: 'var(--spacing-xxl)'
          }}>
          {!visibleSections.aiModels ? (
            <div className="max-w-7xl mx-auto unified-container">
              <SectionSkeleton rows={4} cols={4} variant="card" />
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* 背景装饰 */}
              <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl"></div>
          </div>
          
          <div className="max-w-7xl mx-auto unified-container relative z-10">
                  <motion.div
              initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <Title 
                level={2} 
                className="unified-title-h1 font-black text-white"
                style={{ 
                  color: 'var(--color-bg-white)', 
                  lineHeight: 1.3,
                  marginBottom: 'var(--spacing-xl)'
                }}
              >
                函盖各种创意格式的顶级AI模型
              </Title>
              <Paragraph 
                className="text-lg md:text-xl text-white max-w-4xl mx-auto"
                style={{ color: 'rgba(255, 255, 255, 0.9)' }}
              >
                我们保证顶级的AI模型,例如Veo 3.1、Sora2、Nano Banana、Mureka、ElevenLabs、Claude等
              </Paragraph>
            </motion.div>

            <Row gutter={[24, 24]} justify="center">
              {aiModels.map((model, index) => (
                <Col xs={24} sm={12} lg={6} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 50, scale: 0.9 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ 
                      duration: 0.6, 
                      delay: index * 0.1,
                      type: "spring",
                      stiffness: 100
                    }}
                    whileHover={{ 
                      scale: 1.05, 
                      y: -8,
                      transition: { duration: 0.3 }
                    }}
                    style={{ height: '100%' }}
                  >
                    <Card 
                      className="h-full rounded-2xl overflow-hidden border-0"
                      style={{
                        ...model.bgStyle,
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
                        height: '100%'
                      }}
                      styles={{ 
                        body: { 
                          padding: '32px 28px',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        } 
                      }}
                    >
                      {/* Sora2星空背景效果 */}
                      {index === 0 && (
                        <div 
                          className="absolute inset-0 opacity-30"
                          style={{
                            backgroundImage: `radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.3) 1px, transparent 1px),
                                              radial-gradient(circle at 60% 70%, rgba(255, 255, 255, 0.2) 1px, transparent 1px),
                                              radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.25) 1px, transparent 1px)`,
                            backgroundSize: '50px 50px, 80px 80px, 60px 60px',
                            pointerEvents: 'none'
                          }}
                        />
                      )}
                      
                      {/* Seedream 4.0 漩涡光效背景 */}
                      {index === 3 && (
                        <div 
                          className="absolute inset-0 opacity-40"
                          style={{
                            background: `radial-gradient(circle at 30% 40%, rgba(236, 72, 153, 0.6) 0%, transparent 40%),
                                         radial-gradient(circle at 70% 60%, rgba(168, 85, 247, 0.5) 0%, transparent 40%),
                                         radial-gradient(circle at 50% 20%, rgba(59, 130, 246, 0.4) 0%, transparent 40%),
                                         radial-gradient(circle at 20% 80%, rgba(16, 185, 129, 0.4) 0%, transparent 40%)`,
                            filter: 'blur(60px)',
                            pointerEvents: 'none'
                          }}
                        />
                      )}
                      
                      <div className="relative z-10 flex flex-col items-center text-center h-full">
                        {/* 图标/Logo区域 */}
                        <motion.div 
                          style={{ marginBottom: 'var(--spacing-xl)' }}
                          whileHover={{ 
                            scale: 1.1,
                            rotate: [0, -5, 5, 0],
                            transition: { duration: 0.5 }
                          }}
                        >
                          {model.logo ? (
                            <div className="text-center">
                              <div className="text-white font-medium" style={{
                                fontSize: 'var(--font-body)',
                                marginBottom: 'var(--spacing-sm)'
                              }}>{model.logo}</div>
                              <div className="text-white font-bold" style={{ fontSize: 'var(--font-h3-mobile)' }}>{model.nameEn}</div>
                            </div>
                          ) : (
                            <motion.div 
                              className="w-20 h-20 rounded-full flex items-center justify-center"
                              style={{
                                borderRadius: 'var(--radius-round)',
                                fontSize: 'var(--font-h2-mobile)',
                                background: model.iconBg,
                                backdropFilter: 'blur(10px)'
                              }}
                              animate={{
                                boxShadow: [
                                  '0 0 0px rgba(255, 255, 255, 0)',
                                  '0 0 20px rgba(255, 255, 255, 0.3)',
                                  '0 0 0px rgba(255, 255, 255, 0)'
                                ]
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut"
                              }}
                            >
                              {model.icon}
                            </motion.div>
                          )}
                        </motion.div>
                        
                        {/* 模型名称 */}
                        <Title 
                          level={3} 
                          className="unified-title-h3 font-black"
                          style={{ 
                            marginBottom: 'var(--spacing-lg)',
                            color: model.textColor,
                            textAlign: 'center'
                          }}
                        >
                          {model.name}
                        </Title>
                        
                        {/* 描述文字 */}
                        <Paragraph 
                          className="text-base md:text-lg leading-relaxed flex-grow"
                          style={{ 
                            color: model.textColor,
                            textAlign: 'left',
                            opacity: 0.95
                          }}
                        >
                          {model.description}
                        </Paragraph>
                      </div>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </div>
            </motion.div>
          )}
        </section>

        {/* 核心功能卡片 - banner下方第一个 - 使用渐进式加载 */}
        <section ref={coreFeaturesRef} className="relative bg-gradient-to-b from-transparent via-blue-50/30 to-transparent"
          style={{
            paddingTop: 'var(--spacing-xxl)',
            paddingBottom: 'var(--spacing-xxl)'
          }}>
          {!visibleSections.coreFeatures ? (
            <div className="container-max">
              <SectionSkeleton rows={2} cols={3} variant="card" />
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
            <div className="container-max">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center"
              style={{ marginBottom: 'var(--spacing-xxl)' }}
            >
              <Title 
                level={2} 
                className="text-responsive-xl font-black mb-4"
                style={{
                  background: 'var(--primary-gradient)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: 1.3
                }}
              >
                核心功能
              </Title>
              <Paragraph className="text-responsive-base text-gray-600 max-w-2xl mx-auto">
                集成最新的AI技术，为您提供专业级的创作体验
              </Paragraph>
            </motion.div>

            <Row gutter={[24, 24]}>
              {features.map((feature, index) => (
                <Col xs={24} sm={24} md={8} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 50, scale: 0.9 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ 
                      duration: 0.5, 
                      delay: index * 0.08,
                      type: "spring",
                      stiffness: 120
                    }}
                    whileHover={{ 
                      y: -12, 
                      scale: 1.03,
                      transition: { duration: 0.3 }
                    }}
                  >
                    <Badge.Ribbon text={feature.badge} color={feature.color}>
                      <Card 
                        className="h-full text-center border border-gray-100 overflow-hidden cursor-pointer relative group"
                        onClick={feature.action}
                        style={{
                          borderRadius: '12px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                          background: 'white',
                          transition: 'all 0.3s ease'
                        }}
                        styles={{ body: { padding: '24px 20px' } }}
                        hoverable
                      >
                        {/* 顶部装饰渐变 */}
                        <motion.div 
                          className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400"
                          initial={{ opacity: 0, scaleX: 0 }}
                          whileHover={{ opacity: 1, scaleX: 1 }}
                          transition={{ duration: 0.3 }}
                        />
                        
                        <motion.div 
                          className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5"
                          initial={{ opacity: 0 }}
                          whileHover={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        />
                        
                        <motion.div
                          className="relative z-10"
                          style={{ marginBottom: 'var(--spacing-lg)' }}
                          whileHover={{ 
                            scale: 1.15,
                            rotate: [0, -10, 10, 0],
                            transition: { duration: 0.5 }
                          }}
                          animate={{
                            y: [0, -5, 0],
                          }}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: index * 0.5
                          }}
                        >
                          {feature.icon}
                        </motion.div>
                        
                        <Title level={4} className="unified-title-h4 font-bold relative z-10" style={{ marginBottom: 'var(--spacing-md)' }}>
                          {feature.title}
                        </Title>
                        
                        <Paragraph className="text-gray-600 relative z-10" style={{ 
                          lineHeight: 1.6,
                          fontSize: 'var(--font-small)',
                          marginBottom: 'var(--spacing-lg)'
                        }}>
                          {feature.description}
                        </Paragraph>
                        
                        <motion.div
                          whileHover={{ x: 5 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        >
                        <Button 
                          type="link" 
                          icon={<ArrowRightOutlined />}
                          className="text-blue-500 hover:text-blue-600 font-semibold relative z-10 text-responsive-sm"
                        >
                          立即体验
                        </Button>
                        </motion.div>
                      </Card>
                    </Badge.Ribbon>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </div>
            </motion.div>
          )}
        </section>

        {/* 视频生成演示 - banner下方第二个 - 使用渐进式加载 */}
        <section ref={videoDemoRef} className="bg-gradient-to-b from-white to-gray-50/50 relative" style={{
          paddingTop: 'var(--spacing-xxl)',
          paddingBottom: 'var(--spacing-xxl)'
        }}>
          {!visibleSections.videoDemo ? (
            <div className="container-max">
              <SectionSkeleton rows={3} cols={2} variant="text" />
            </div>
          ) : (
            <>
              {/* 装饰背景 */}
              <div className="absolute inset-0 overflow-hidden opacity-40">
                <div className="absolute top-20 right-10 w-64 h-64 bg-blue-200 rounded-full filter blur-3xl"></div>
                <div className="absolute bottom-20 left-10 w-64 h-64 bg-purple-200 rounded-full filter blur-3xl"></div>
              </div>
              
              <div className="container-max relative z-10">
            <Row gutter={[64, 64]} align="middle">
              <Col xs={24} lg={12}>
                <motion.div
                  initial={{ opacity: 0, x: -80, scale: 0.95 }}
                  whileInView={{ opacity: 1, x: 0, scale: 1 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ 
                    duration: 0.8,
                    type: "spring",
                    stiffness: 100
                  }}
                >
                  <div style={{ marginBottom: 'var(--spacing-xl)' }}>
                    <Badge count="NEW" style={{ backgroundColor: 'var(--color-success)' }}>
                      <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-4 py-2 rounded-full">
                        🎬 视频生成技术
                      </span>
                    </Badge>
                  </div>
                  
                  <Title level={2} className="unified-title-h1 font-black" style={{ 
                    lineHeight: 1.3,
                    marginBottom: 'var(--spacing-xl)'
                  }}>
                    体验AI创作的
                    <br />
                    <span style={{
                      background: 'var(--primary-gradient)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>
                      无限魅力
                    </span>
                  </Title>
                  
                  <Paragraph className="text-gray-600 leading-relaxed" style={{
                    fontSize: 'var(--font-h4-desktop)',
                    marginBottom: 'var(--spacing-xl)'
                  }}>
                    只需简单的文字描述，我们的AI就能为您创造出令人惊艳的视频作品。
                    无论是商业用途还是个人创作，都能满足您的需求。
                  </Paragraph>

                  <div style={{
                    marginBottom: 'var(--spacing-xl)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-lg)'
                  }}>
                    {['文字转视频，秒级生成', '多种风格，自由选择', '4K高清，专业品质', '商用授权，放心使用'].map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -30, scale: 0.9 }}
                        whileInView={{ opacity: 1, x: 0, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ 
                          duration: 0.5, 
                          delay: i * 0.1,
                          type: "spring",
                          stiffness: 150
                        }}
                        whileHover={{ 
                          x: 5,
                          transition: { duration: 0.2 }
                        }}
                        className="flex items-center"
                        style={{ gap: 'var(--spacing-md)' }}
                      >
                        <motion.div
                          animate={{ 
                            scale: [1, 1.2, 1],
                            rotate: [0, 360]
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: i * 0.3
                          }}
                      >
                        <CheckCircleOutlined className="text-green-500" style={{ fontSize: 'var(--font-h4-mobile)' }} />
                        </motion.div>
                        <Text className="text-gray-700" style={{ fontSize: 'var(--font-body)' }}>{item}</Text>
                      </motion.div>
                    ))}
                  </div>
                  
                  <Space size="large" wrap>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button 
                        type="primary" 
                        size="large"
                        icon={<PlayCircleOutlined />}
                        onClick={() => navigate('/video')}
                        className="unified-button text-lg font-semibold"
                        style={{
                          paddingLeft: 'var(--button-padding-h)',
                          paddingRight: 'var(--button-padding-h)',
                          background: 'var(--primary-gradient)',
                          border: 'none',
                          boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)'
                        }}
                      >
                        立即体验
                      </Button>
                    </motion.div>
                    
                    <Button 
                      size="large"
                      onClick={() => navigate('/history')}
                        className="unified-button text-lg font-semibold"
                        style={{
                          paddingLeft: 'var(--button-padding-h)',
                          paddingRight: 'var(--button-padding-h)'
                        }}
                    >
                      查看示例
                    </Button>
                  </Space>
                </motion.div>
              </Col>
              
              <Col xs={24} lg={12}>
                <motion.div
                  initial={{ opacity: 0, x: 80, scale: 0.95 }}
                  whileInView={{ opacity: 1, x: 0, scale: 1 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ 
                    duration: 0.8,
                    type: "spring",
                    stiffness: 100
                  }}
                  className="relative"
                >
                  <motion.div
                    whileHover={{ 
                      scale: 1.05, 
                      rotate: 2,
                      transition: { duration: 0.3 }
                    }}
                    transition={{ type: "spring", stiffness: 200 }}
                    className="relative rounded-3xl overflow-hidden shadow-2xl"
                    style={{
                      background: 'linear-gradient(135deg, #a855f7 0%, #667eea 100%)',
                      padding: '2px'
                    }}
                  >
                    <div className="bg-white text-center" style={{
                      padding: '48px 32px',
                      borderRadius: '24px'
                    }}>
                      <div style={{ marginBottom: '32px' }}>
                        <VideoCameraOutlined style={{
                          fontSize: '64px',
                          color: '#1f2937',
                          display: 'block',
                          margin: '0 auto'
                        }} />
                      </div>
                      
                      <Title level={3} className="unified-title-h3" style={{ 
                        marginBottom: '16px',
                        fontWeight: 700,
                        color: '#1f2937'
                      }}>
                        Powered by OpenAI Sora2
                      </Title>
                      
                      <Paragraph className="text-gray-600" style={{
                        fontSize: '14px',
                        marginBottom: '32px',
                        lineHeight: 1.6
                      }}>
                        基于最先进的AI技术，为您提供最优质的生成体验
                      </Paragraph>
                      
                      <div className="grid grid-cols-2" style={{
                        gap: '16px',
                        marginBottom: '32px'
                      }}>
                        <div style={{
                          borderRadius: '16px',
                          padding: '20px 16px',
                          background: '#eff6ff',
                          textAlign: 'center'
                        }}>
                          <div style={{
                            fontSize: '24px',
                            fontWeight: 900,
                            color: '#3b82f6',
                            marginBottom: '8px'
                          }}>60秒</div>
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280'
                          }}>平均生成时间</div>
                        </div>
                        <div style={{
                          borderRadius: '16px',
                          padding: '20px 16px',
                          background: '#f3e8ff',
                          textAlign: 'center'
                        }}>
                          <div style={{
                            fontSize: '24px',
                            fontWeight: 900,
                            color: '#9333ea',
                            marginBottom: '8px'
                          }}>4K</div>
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280'
                          }}>超清画质</div>
                        </div>
                      </div>
                      
                      <Button 
                        type="primary" 
                        size="large"
                        block
                        onClick={() => navigate('/video')}
                        className="text-lg font-semibold"
                        style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          border: 'none',
                          height: '48px',
                          borderRadius: '12px'
                        }}
                      >
                        开始创作 →
                      </Button>
                    </div>
                  </motion.div>
                </motion.div>
              </Col>
            </Row>
          </div>
            </>
          )}
        </section>

        {/* 图片生成演示 - banner下方第二个 - 使用 Intersection Observer 懒加载 */}
        <section ref={imageDemoRef} className="py-20 md:py-28 bg-gradient-to-b from-gray-50/50 to-white relative">
          {!visibleSections.imageDemo ? (
            <div className="container-max">
              <SectionSkeleton rows={3} cols={2} variant="text" />
            </div>
          ) : (
            <>
              {/* 装饰背景 */}
              <div className="absolute inset-0 overflow-hidden opacity-40">
                <div className="absolute top-20 left-10 w-64 h-64 bg-pink-200 rounded-full filter blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-64 h-64 bg-orange-200 rounded-full filter blur-3xl"></div>
              </div>
              
              <div className="container-max relative z-10">
                <Row gutter={[64, 64]} align="middle">
                  <Col xs={24} lg={12} className="order-2 lg:order-1">
                    <motion.div
                      initial={{ opacity: 0, x: -80, scale: 0.95 }}
                      whileInView={{ opacity: 1, x: 0, scale: 1 }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{ 
                        duration: 0.8,
                        type: "spring",
                        stiffness: 100
                      }}
                      className="relative"
                    >
                      <motion.div
                        whileHover={{ 
                          scale: 1.05, 
                          rotate: -2,
                          transition: { duration: 0.3 }
                        }}
                        transition={{ type: "spring", stiffness: 200 }}
                        className="relative rounded-3xl overflow-hidden"
                        style={{
                          background: 'linear-gradient(135deg, rgba(240, 147, 251, 0.15) 0%, rgba(245, 87, 108, 0.15) 50%, rgba(79, 172, 254, 0.15) 100%)',
                          padding: '1.5px',
                          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        <div className="bg-white text-center" style={{
                          padding: '48px 32px',
                          borderRadius: '24px',
                          height: '100%'
                        }}>
                          <div style={{ marginBottom: '32px' }}>
                            <PictureOutlined style={{
                              fontSize: '64px',
                              color: '#1f2937',
                              display: 'block',
                              margin: '0 auto'
                            }} />
                          </div>
                          
                          <Title level={3} className="unified-title-h3" style={{ 
                            marginBottom: '16px',
                            fontWeight: 700,
                            color: '#1f2937'
                          }}>
                            AI图像生成引擎
                          </Title>
                          
                          <Paragraph className="text-gray-600" style={{
                            fontSize: '14px',
                            marginBottom: '32px',
                            lineHeight: 1.6
                          }}>
                            多种模型可选，满足不同创作需求
                          </Paragraph>
                          
                          <div className="grid grid-cols-2" style={{
                            gap: '16px',
                            marginBottom: '32px'
                          }}>
                            <div style={{
                              borderRadius: '16px',
                              padding: '20px 16px',
                              background: '#fce7f3',
                              textAlign: 'center'
                            }}>
                              <div style={{
                                fontSize: '24px',
                                fontWeight: 900,
                                color: '#ec4899',
                                marginBottom: '8px'
                              }}>30秒</div>
                              <div style={{
                                fontSize: '12px',
                                color: '#6b7280'
                              }}>快速生成</div>
                            </div>
                            <div style={{
                              borderRadius: '16px',
                              padding: '20px 16px',
                              background: '#fff7ed',
                              textAlign: 'center'
                            }}>
                              <div style={{
                                fontSize: '24px',
                                fontWeight: 900,
                                color: '#f97316',
                                marginBottom: '8px'
                              }}>高清</div>
                              <div style={{
                                fontSize: '12px',
                                color: '#6b7280'
                              }}>精美画质</div>
                            </div>
                          </div>
                          
                          <Button 
                            type="primary" 
                            size="large"
                            block
                            onClick={() => navigate('/image')}
                            className="text-lg font-semibold"
                            style={{
                              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                              border: 'none',
                              height: '48px',
                              borderRadius: '12px'
                            }}
                          >
                            开始创作 →
                          </Button>
                        </div>
                      </motion.div>
                    </motion.div>
                  </Col>
                  
                  <Col xs={24} lg={12} className="order-1 lg:order-2">
                    <motion.div
                      initial={{ opacity: 0, x: 80, scale: 0.95 }}
                      whileInView={{ opacity: 1, x: 0, scale: 1 }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{ 
                        duration: 0.8,
                        type: "spring",
                        stiffness: 100
                      }}
                    >
                      <div style={{ marginBottom: 'var(--spacing-xl)' }}>
                        <Badge count="HOT" style={{ backgroundColor: 'var(--color-error)' }}>
                          <span className="text-sm font-semibold text-pink-600 bg-pink-50 px-4 py-2 rounded-full">
                            🎨 AI图像生成
                          </span>
                        </Badge>
                      </div>
                      
                      <Title level={2} className="unified-title-h1 font-black" style={{ 
                    lineHeight: 1.3,
                    marginBottom: 'var(--spacing-xl)'
                  }}>
                        让创意瞬间
                        <br />
                        <span style={{
                          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}>
                          化为现实
                        </span>
                      </Title>
                      
                      <Paragraph className="text-gray-600 leading-relaxed" style={{
                    fontSize: 'var(--font-h4-desktop)',
                    marginBottom: 'var(--spacing-xl)'
                  }}>
                        强大的AI图像生成能力，从概念到作品只需几分钟。
                        支持多种风格和尺寸，满足您的各种创作需求。
                      </Paragraph>

                      <div style={{
                    marginBottom: 'var(--spacing-xl)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-lg)'
                  }}>
                        {['多种模型，自由选择', '多种尺寸，灵活适配', '精美画质，专业级输出', '快速生成，即时预览'].map((item, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            className="flex items-center"
                        style={{ gap: 'var(--spacing-md)' }}
                          >
                            <CheckCircleOutlined className="text-pink-500" style={{ fontSize: 'var(--font-h4-mobile)' }} />
                            <Text className="text-gray-700" style={{ fontSize: 'var(--font-body)' }}>{item}</Text>
                          </motion.div>
                        ))}
                      </div>
                      
                      <Space size="large" wrap>
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                          <Button 
                            type="primary" 
                            size="large"
                            icon={<PictureOutlined />}
                            onClick={() => navigate('/image')}
                            className="unified-button text-lg font-semibold"
                            style={{
                              paddingLeft: 'var(--button-padding-h)',
                              paddingRight: 'var(--button-padding-h)',
                              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                              border: 'none',
                              boxShadow: '0 10px 30px rgba(240, 147, 251, 0.4)'
                            }}
                          >
                            立即体验
                          </Button>
                        </motion.div>
                        
                        <Button 
                          size="large"
                          onClick={() => navigate('/history')}
                          className="unified-button text-lg font-semibold"
                        style={{
                          paddingLeft: 'var(--button-padding-h)',
                          paddingRight: 'var(--button-padding-h)'
                        }}
                        >
                          查看示例
                        </Button>
                      </Space>
                    </motion.div>
                  </Col>
                </Row>
              </div>
            </>
          )}
        </section>

        {/* 提示词润色补全 - banner下方第三个 - 使用 Intersection Observer 懒加载 */}
        <section ref={promptPolishRef} className="py-20 md:py-28 bg-gradient-to-b from-white to-gray-50/50 relative">
          {!visibleSections.promptPolish ? (
            <div className="container-max">
              <SectionSkeleton rows={3} cols={2} variant="text" />
            </div>
          ) : (
            <>
              {/* 装饰背景 */}
              <div className="absolute inset-0 overflow-hidden opacity-40">
                <div className="absolute top-20 right-1/4 w-64 h-64 bg-yellow-200 rounded-full filter blur-3xl"></div>
                <div className="absolute bottom-20 left-1/4 w-64 h-64 bg-green-200 rounded-full filter blur-3xl"></div>
              </div>
              
              <div className="container-max relative z-10">
                <Row gutter={[64, 64]} align="middle">
                  <Col xs={24} lg={12}>
                    <motion.div
                      initial={{ opacity: 0, x: -50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8 }}
                    >
                      <div style={{ marginBottom: 'var(--spacing-xl)' }}>
                        <Badge count="AI" style={{ backgroundColor: 'var(--color-info)' }}>
                          <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-4 py-2 rounded-full">
                            ✨ 智能提示词优化
                          </span>
                        </Badge>
                      </div>
                      
                      <Title level={2} className="unified-title-h1 font-black" style={{ 
                    lineHeight: 1.3,
                    marginBottom: 'var(--spacing-xl)'
                  }}>
                        让AI帮您
                        <br />
                        <span style={{
                          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}>
                          完善创意
                        </span>
                      </Title>
                      
                      <Paragraph className="text-gray-600 leading-relaxed" style={{
                    fontSize: 'var(--font-h4-desktop)',
                    marginBottom: 'var(--spacing-xl)'
                  }}>
                        不知道如何描述您的想法？输入简单的关键词，AI会自动润色和补全您的提示词，
                        让您的创作更加精准和专业。
                      </Paragraph>

                      <div style={{
                    marginBottom: 'var(--spacing-xl)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-lg)'
                  }}>
                        {['智能润色，专业优化', '自动补全，丰富细节', '支持图片和视频', '一键生成完美提示词'].map((item, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            className="flex items-center"
                        style={{ gap: 'var(--spacing-md)' }}
                          >
                            <CheckCircleOutlined className="text-blue-500" style={{ fontSize: 'var(--font-h4-mobile)' }} />
                            <Text className="text-gray-700" style={{ fontSize: 'var(--font-body)' }}>{item}</Text>
                          </motion.div>
                        ))}
                      </div>
                      
                      <Space size="large" wrap>
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                          <Button 
                            type="primary" 
                            size="large"
                            icon={<EditOutlined />}
                            onClick={() => navigate('/video')}
                            className="unified-button text-lg font-semibold"
                            style={{
                              paddingLeft: 'var(--button-padding-h)',
                              paddingRight: 'var(--button-padding-h)',
                              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                              border: 'none',
                              boxShadow: '0 10px 30px rgba(79, 172, 254, 0.4)'
                            }}
                          >
                            立即体验
                          </Button>
                        </motion.div>
                        
                        <Button 
                          size="large"
                          onClick={() => navigate('/image')}
                          className="unified-button text-lg font-semibold"
                        style={{
                          paddingLeft: 'var(--button-padding-h)',
                          paddingRight: 'var(--button-padding-h)'
                        }}
                        >
                          图片生成
                        </Button>
                      </Space>
                    </motion.div>
                  </Col>
                  
                  <Col xs={24} lg={12}>
                    <motion.div
                      initial={{ opacity: 0, x: 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8 }}
                      className="relative"
                    >
                      <motion.div
                        whileHover={{ scale: 1.03, rotate: 1 }}
                        transition={{ type: "spring", stiffness: 200 }}
                        className="relative rounded-3xl overflow-hidden shadow-2xl"
                        style={{
                          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 50%, #43e97b 100%)',
                          padding: '2px'
                        }}
                      >
                        <div className="bg-white rounded-3xl p-12">
                          <div style={{ marginBottom: 'var(--spacing-xl)' }}>
                            <div className="bg-gray-50" style={{
                              borderRadius: 'var(--radius-xl)',
                              padding: 'var(--spacing-lg)',
                              marginBottom: 'var(--spacing-lg)'
                            }}>
                              <div style={{
                                fontSize: 'var(--font-small)',
                                color: 'var(--color-text-tertiary)',
                                marginBottom: 'var(--spacing-sm)'
                              }}>原始提示词</div>
                              <div style={{
                                fontSize: 'var(--font-body)',
                                color: 'var(--color-text-primary)',
                                fontWeight: 500
                              }}>一只猫</div>
                            </div>
                            <div className="flex items-center justify-center" style={{ marginBottom: 'var(--spacing-lg)' }}>
                              <ArrowRightOutlined style={{ fontSize: 'var(--font-h4-mobile)' }} className="text-blue-500" />
                            </div>
                            <div className="bg-blue-50 border-2 border-blue-200" style={{
                              borderRadius: 'var(--radius-xl)',
                              padding: 'var(--spacing-lg)'
                            }}>
                              <div style={{
                                fontSize: 'var(--font-small)',
                                color: 'var(--color-info)',
                                marginBottom: 'var(--spacing-sm)',
                                fontWeight: 600
                              }}>AI优化后</div>
                              <div className="text-sm text-gray-800 leading-relaxed">
                                一只毛茸茸的橙色虎斑猫，有着明亮的绿色眼睛，优雅地坐在木制窗台上，沐浴在温暖的金色阳光下，背景柔和模糊，专业摄影，8K高清，细节丰富的毛发纹理
                              </div>
                            </div>
                          </div>
                          
                          <motion.div
                            animate={{ 
                              scale: [1, 1.05, 1],
                            }}
                            transition={{ 
                              duration: 2, 
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                            className="text-center mb-6"
                          >
                            <BulbOutlined className="text-6xl" style={{
                              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                            }} />
                          </motion.div>
                          
                          <Title level={3} className="unified-title-h3 text-center" style={{ marginBottom: 'var(--spacing-lg)' }}>
                            智能提示词优化
                          </Title>
                          
                          <Paragraph className="text-gray-600 mb-8 text-lg text-center">
                            基于深度学习的AI技术，让您的创意描述更加专业和详细
                          </Paragraph>
                          
                          <div className="grid grid-cols-3 gap-4 mb-8">
                            <div className="bg-blue-50 rounded-xl p-3 text-center">
                              <div className="text-2xl font-black text-blue-600">AI</div>
                              <div className="text-xs text-gray-600">智能优化</div>
                            </div>
                            <div className="bg-green-50 rounded-xl p-3 text-center">
                              <div className="text-2xl font-black text-green-600">秒</div>
                              <div className="text-xs text-gray-600">快速响应</div>
                            </div>
                            <div className="bg-purple-50 rounded-xl p-3 text-center">
                              <div className="text-2xl font-black text-purple-600">+</div>
                              <div className="text-xs text-gray-600">丰富细节</div>
                            </div>
                          </div>
                          
                          <Button 
                            type="primary" 
                            size="large"
                            block
                            icon={<EditOutlined />}
                            onClick={() => navigate('/video')}
                            className="unified-button text-lg font-semibold"
                            style={{
                              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                              border: 'none'
                            }}
                          >
                            开始优化 →
                          </Button>
                        </div>
                      </motion.div>
                    </motion.div>
                  </Col>
                </Row>
              </div>
            </>
          )}
        </section>

        {/* 用户评价 - 优化设计，同时展示3个 - 使用 Intersection Observer 懒加载 */}
        <section ref={userReviewsRef} className="py-16 md:py-24 relative bg-gradient-to-b from-gray-50/50 to-white">
          {!visibleSections.userReviews ? (
            <div className="container-max">
              <SectionSkeleton rows={2} cols={3} variant="card" />
            </div>
          ) : (
            <>
              <div className="container-max">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="text-center mb-12"
                >
                  <Title 
                    level={2} 
                    className="text-responsive-xl font-black mb-4"
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      lineHeight: 1.3
                    }}
                  >
                    用户真实评价
                  </Title>
                  <Paragraph className="text-responsive-base text-gray-600">
                    来自各行各业创作者的真实反馈
                  </Paragraph>
                </motion.div>

                <Row gutter={[24, 24]}>
                  {testimonials.map((testimonial, index) => (
                    <Col xs={24} md={8} key={index}>
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                      >
                        <Card 
                          className="text-center border border-gray-100 h-full overflow-hidden"
                          style={{
                            background: '#ffffff',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                            borderRadius: '16px'
                          }}
                          styles={{ body: { padding: '32px 28px' } }}
                        >
                          <div className="flex flex-col items-center">
                            <div className="mb-4">
                              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center text-3xl mx-auto">
                                {testimonial.avatar}
                              </div>
                            </div>
                            <div className="mb-3">
                              {[1,2,3,4,5].map((star) => (
                                <StarOutlined key={star} className="text-yellow-400 text-sm" />
                              ))}
                            </div>
                            <Paragraph className="text-responsive-sm text-gray-700 mb-4 italic" style={{ lineHeight: 1.6 }}>
                              "{testimonial.content}"
                            </Paragraph>
                            <div>
                              <Title level={5} className="mb-1 text-responsive-base font-bold">{testimonial.name}</Title>
                              <Text className="text-gray-500 text-responsive-sm">{testimonial.role}</Text>
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    </Col>
                  ))}
                </Row>
              </div>
            </>
          )}
        </section>

        {/* 统计信息 - 移到优势展示下方 - 使用 Intersection Observer 懒加载 */}
        <section ref={statsRef} className="py-12 md:py-16 bg-white/90 backdrop-blur-md relative">
          {!visibleSections.stats ? (
            <div className="container-max">
              <SectionSkeleton variant="stat" />
            </div>
          ) : (
            <>
              {/* 顶部装饰线 */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-30"></div>
              
              <div className="container-max">
                <Row gutter={[32, 32]}>
                  {stats.map((stat, index) => (
                    <Col xs={12} sm={6} key={index}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        {...COMMON_STYLES.cardHover}
                      >
                        <Card 
                          className="unified-card text-center overflow-hidden relative"
                          styles={{ body: { padding: '20px 16px' } }}
                        >
                          {/* 顶部装饰条 */}
                          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-purple-500 opacity-60"></div>
                          
                          <Statistic
                            title={<span className="unified-text-secondary font-semibold text-responsive-sm">{stat.title}</span>}
                            value={stat.value}
                            suffix={<span className="text-responsive-base">{stat.suffix}</span>}
                            valueStyle={{ 
                              background: GlobalStyles.colors.primary,
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              fontSize: 'clamp(1.5rem, 5vw, 2.5rem)', 
                              fontWeight: '900' 
                            }}
                          />
                        </Card>
                      </motion.div>
                    </Col>
                  ))}
                </Row>
              </div>
            </>
          )}
        </section>

        {/* CTA区域 - 全新设计 - 使用 Intersection Observer 懒加载 */}
        <section ref={ctaRef} className="py-24 md:py-32 relative overflow-hidden">
          {!visibleSections.cta ? (
            <div className="container-max text-center">
              <SectionSkeleton rows={3} cols={1} variant="text" />
            </div>
          ) : (
            <>
              <div 
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                }}
              >
                {/* 网格背景 */}
                <div className="absolute inset-0 opacity-10" style={{
                  backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                  backgroundSize: '50px 50px'
                }}></div>
              </div>
              
              {/* 装饰性元素 */}
              <div className="absolute inset-0 overflow-hidden">
            <motion.div
              className="absolute w-96 h-96 bg-white rounded-full opacity-10"
              animate={{
                scale: [1, 1.2, 1],
                x: [0, 50, 0],
                y: [0, 30, 0]
              }}
              transition={{ duration: 8, repeat: Infinity }}
              style={{ top: '-20%', right: '-10%' }}
            />
            <motion.div
              className="absolute w-96 h-96 bg-white rounded-full opacity-10"
              animate={{
                scale: [1, 1.3, 1],
                x: [0, -50, 0],
                y: [0, -30, 0]
              }}
              transition={{ duration: 10, repeat: Infinity }}
              style={{ bottom: '-20%', left: '-10%' }}
            />
          </div>

          <div className="container-max text-center relative z-10" style={{ maxWidth: '1280px' }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Title level={2} className="unified-title-h1 text-white font-black" style={{ marginBottom: 'var(--spacing-xl)' }}>
                准备好开始您的
                <br />
                AI创作之旅了吗？
              </Title>
              
              <Paragraph className="text-2xl text-white/90 mb-12 leading-relaxed">
                加入全球<strong className="text-yellow-300">10,000+</strong>创作者的行列
                <br />
                体验最先进的AI视频和图像生成技术
              </Paragraph>
              
              <Space size="large" wrap className="mb-12">
                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                  <Button 
                    type="primary"
                    size="large"
                    icon={<RocketOutlined />}
                    onClick={() => navigate('/register')}
                    className="h-16 px-12 text-xl font-extrabold rounded-xl"
                    style={{
                      background: 'linear-gradient(135deg, #ff7a18 0%, #af002d 100%)',
                      color: '#ffffff',
                      border: 'none',
                      boxShadow: '0 12px 40px rgba(175, 0, 45, 0.35)'
                    }}
                  >
                    免费开始创作
                  </Button>
                </motion.div>
                
                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                  <Button 
                    size="large"
                    icon={<PlayCircleOutlined />}
                    onClick={() => navigate('/video')}
                    className="h-16 px-12 text-xl font-bold rounded-xl border-2 border-white text-white hover:bg-white hover:text-purple-600"
                    style={{
                      background: 'transparent',
                    }}
                  >
                    观看演示
                  </Button>
                </motion.div>
              </Space>

              <div className="flex justify-center gap-12 text-white/80 text-sm flex-wrap">
                <span className="flex items-center gap-2">
                  <CheckCircleOutlined className="text-green-300" /> 无需信用卡
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircleOutlined className="text-green-300" /> 免费试用额度
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircleOutlined className="text-green-300" /> 随时取消
                </span>
              </div>
            </motion.div>
          </div>
            </>
          )}
        </section>
      </div>
    </div>
  )
}

export default HomePage
